import * as React from 'react';
import { useActions } from '../viewModalActions';
import { View } from '../../../types/View';
import { Schema } from '../../../types/Metadata';
import { getParentTypes, getSchema, parseField } from '../../../utils';
import { Toggler } from '../../Toggler/toggler';
import { SchemaFieldTypeahead } from '../../SchemaFieldTypeahead/schemaFieldTypeahead';
import { ModalGroup, ModalGroups } from '../../ModalGroup/modalGroup';
import { FieldType } from '../../../enums/FieldType';
import { DataType } from '../../../enums/DataType';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { useSelector } from '../../../utils/useSelector';

export const PageConfigure = () => {
  const groupBys = useSelector(s => s.viewModal?.groupBy || []);
  const isMyItemsView = useSelector(s => s.viewModal?.isMyItemsView);
  const parentView = useSelector(s => s.viewModal?.parentView);
  const isBoardWithSwimlanes = useSelector(
    s => s?.viewModal?.isBoardWithSwimlanes
  );
  const { setViewSetting } = useActions();
  [{ field: null }, { field: null }].forEach(g => {
    if (groupBys.length < 2) groupBys.push(g);
  });

  const columnGroupBy = groupBys[0];
  const swimlaneGroupBy = groupBys[1];
  const onChangeGroupBy = React.useCallback(
    (g: Partial<View.GroupByField>) => {
      const groupBy = groupBys.map((groupBy, i) => {
        if (0 != i) return groupBy;
        return { ...groupBy, ...g };
      });
      if (!deepEquals(groupBys, groupBy, true)) {
        setViewSetting({ groupBy });
      }
    },
    [groupBys, setViewSetting]
  );

  return (
    <ModalGroups>
      <ModalGroup label="Configure Board">
        <GroupBy groupBy={columnGroupBy} idx={0} onChange={onChangeGroupBy} />
        <ColumnGrouping groupBy={columnGroupBy} onChange={onChangeGroupBy} />
        <div className="toggle-field">
          <div>Enable Swimlanes</div>
          <Toggler
            checked={isBoardWithSwimlanes}
            disabled={isMyItemsView || !!parentView}
            onChange={isBoardWithSwimlanes => {
              let updatedState = { isBoardWithSwimlanes };
              if (!isBoardWithSwimlanes) {
                updatedState['groupBy'] = groupBys.splice(0, 1);
              }
              setViewSetting(updatedState);
            }}
          />
        </div>
        {isBoardWithSwimlanes && (
          <GroupBy
            groupBy={swimlaneGroupBy}
            idx={1}
            onChange={(g: Partial<View.GroupByField>) => {
              const groupBy = groupBys.map((groupBy, i) => {
                if (1 != i) return groupBy;
                return { ...groupBy, ...g };
              });
              if (!deepEquals(groupBys, groupBy)) {
                setViewSetting({ groupBy });
              }
            }}
          />
        )}
      </ModalGroup>
    </ModalGroups>
  );
};

const getColumnGroupingTypes = (
  groupBy: View.GroupByField,
  schemas: Schema[]
) => {
  const { schema, referenceSchema } = parseField(groupBy.field, schemas);
  return referenceSchema
    ? getParentTypes(referenceSchema, schemas)
    : getParentTypes(schema, schemas);
};

type GroupByProps = {
  groupBy: View.GroupByField;
  idx: number;
  onChange: (p: Partial<View.GroupByField>) => any;
};
const GroupBy = ({ groupBy, onChange, idx }: GroupByProps) => {
  const isMyItemsView = useSelector(s => s.viewModal?.isMyItemsView);
  const parentView = useSelector(s => s.viewModal?.parentView);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const groupByField = parseField(groupBy?.field, schemas);
  const groupSortFields = getGroupSortFields(groupBy, schemas);
  const showColumnGroupings = groupBy.groupByParentField !== undefined;
  const groupingField = React.useMemo(() => {
    if (!groupBy.groupByParentField) return null;
    return parseField(groupBy.groupByParentField, schemas);
  }, [groupBy.groupByParentField, schemas]);

  const excludeFields = React.useCallback(f => {
    return !(
      f.fieldType == FieldType.reference ||
      f.dataType == DataType.string ||
      f.fieldType == FieldType.computed
    );
  }, []);
  const onSelectField = React.useCallback(
    f => {
      if (idx == 0 && showColumnGroupings && groupingField) {
        const newGroupings = getColumnGroupingTypes({ field: f?.key }, schemas);
        if (!newGroupings.includes(groupingField.schemaType)) {
          onChange({
            field: f?.key,
            groupByParentField: newGroupings.length > 0 ? null : undefined
          });
          return;
        }
      }
      onChange({
        field: f?.key
      });
    },
    [onChange, showColumnGroupings, groupingField]
  );
  const onTextChange = React.useCallback(
    t => {
      if (idx == 1 && !t && !!groupBy.field) {
        onChange({ field: null });
      }
    },
    [idx, onChange, groupBy]
  );

  return (
    <>
      <div>
        <label>{idx == 0 ? 'Select Column Header' : 'Select Swimlane'}</label>
        <SchemaFieldTypeahead
          selected={
            groupBy?.field == 'Tasks'
              ? groupBy.field
              : groupByField?.schemaField?.displayName
          }
          onSelectField={onSelectField}
          disabled={isMyItemsView || !!parentView}
          onTextChange={onTextChange}
          excludeFields={excludeFields}
        />
      </div>
      {groupSortFields?.length && (
        <div>
          <label>Sort By</label>
          <div className="input-group">
            <select
              disabled={
                !groupByField?.schemaField || isMyItemsView || !!parentView
              }
              className="form-control"
              value={groupBy?.orderBy}
              onChange={e => onChange({ orderBy: e.target.value })}
            >
              {groupSortFields.map(({ schemaField }) => (
                <option key={schemaField.key} value={schemaField.key}>
                  {schemaField.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {!!groupBy?.field && (
        <div className="toggle-field">
          <div>Show {idx == 0 ? 'column' : 'swimlane'} without any cards</div>
          <Toggler
            checked={groupBy?.showGroupsWithoutItem}
            disabled={isMyItemsView || !!parentView}
            onChange={showGroupsWithoutItem =>
              onChange({ showGroupsWithoutItem })
            }
          />
        </div>
      )}
    </>
  );
};

const ColumnGrouping = ({
  groupBy,
  onChange
}: Pick<GroupByProps, 'groupBy' | 'onChange'>) => {
  const isMyItemsView = useSelector(s => s.viewModal?.isMyItemsView);
  const parentView = useSelector(s => s.viewModal?.parentView);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const showColumnGroupings = groupBy.groupByParentField !== undefined;
  const groupingField = React.useMemo(() => {
    if (!groupBy.groupByParentField) return null;
    return parseField(groupBy.groupByParentField, schemas);
  }, [groupBy.groupByParentField, schemas]);
  const groupingSortFields = React.useMemo(
    () => getGroupSortFields({ field: groupBy.groupByParentField }, schemas),
    [groupBy.groupByParentField, schemas]
  );
  const columnGroupingTypes = React.useMemo(
    () => getColumnGroupingTypes(groupBy, schemas),
    [groupBy.field, schemas]
  );

  if (!showColumnGroupings && !columnGroupingTypes?.length) return null;

  return (
    <>
      <div className="toggle-field">
        <div>Enable Column Grouping</div>
        <Toggler
          checked={showColumnGroupings}
          disabled={!!parentView}
          onChange={e => onChange({ groupByParentField: e ? null : undefined })}
        />
      </div>
      {showColumnGroupings && (
        <div>
          <label>Select Column Grouping</label>
          <SchemaFieldTypeahead
            selected={groupingField?.schemaField?.displayName}
            onSelectField={f => {
              onChange({
                groupByParentField: f?.key || null
              });
            }}
            removable
            disabled={isMyItemsView || !!parentView}
            excludeFields={(f, s) => {
              if (!s) return true;
              return !(
                columnGroupingTypes.includes(s.props.type) &&
                f.field == s.props.displayField
              );
            }}
          />
        </div>
      )}
      {groupingSortFields?.length && (
        <div>
          <label>Sort By</label>
          <div className="input-group">
            <select
              disabled={
                !groupingField?.schemaField || isMyItemsView || !!parentView
              }
              className="form-control"
              value={groupBy?.groupByParentOrderBy}
              onChange={e => onChange({ groupByParentOrderBy: e.target.value })}
            >
              {groupingSortFields.map(({ schemaField }) => (
                <option key={schemaField.key} value={schemaField.key}>
                  {schemaField.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );
};

const getGroupSortFields = (groupBy: View.GroupByField, schemas: Schema[]) => {
  if (!groupBy?.field) return null;
  let { schema, schemaField } = parseField(groupBy.field, schemas);
  if (schemaField?.fieldType == FieldType.reference) {
    schema = getSchema(schemas, schemaField.dataType);
  }
  if (!schema) return null;
  return schema.props.fields
    .reduce((p, f) => {
      if (f.fieldType == FieldType.reference) return p;
      if (f.field == schema.props.displayField) {
        p.unshift(parseField(f.key, schemas));
      } else {
        p.push(parseField(f.key, schemas));
      }
      return p;
    }, [])
    .filter(x => x?.schemaField);
};
